import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronRightIcon, DotFilledIcon } from '@radix-ui/react-icons';
import { useSubmit } from 'react-router-dom';
import { Avatar } from '@scholastic/mockingjay';
import { useGetMeApi } from 'eds-common/api/v2';
import ProfileAvatarPlaceholder from '@/assets/icons/profile-avatar-placeholder.svg';
import {
  useSwitchOrganization,
  type UserOrg,
} from '@/hooks/use-switch-organization/use-switch-organization';
import { SwitchingOverlay } from '@/components/ProfileMenu/SwitchingOverlay';

const TRIGGER_CLASSES =
  'flex h-11 w-11 items-center justify-center rounded-full bg-brand-white shadow-md focus-ring hover:bg-neutral-100';

const AVATAR_CLASSES = 'h-11 w-11 bg-neutral-100 uppercase';

const MENU_ITEM_CLASSES =
  'cursor-pointer px-5 py-2 text-lg font-light leading-[18px] outline-none data-[highlighted]:bg-neutral-1100 data-[highlighted]:text-brand-white';

const MENU_CONTENT_CLASSES =
  'space-y-4 overflow-hidden rounded-lg bg-white py-5 shadow-[0_4px_24px_0_rgba(0,0,0,0.25)]';

interface OrgSubMenuProps {
  orgs: UserOrg[];
  currentOrgId?: string;
  onSelectOrg: (orgId: string, orgName: string) => void;
}

function OrgSubMenu({ orgs, currentOrgId, onSelectOrg }: OrgSubMenuProps) {
  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger
        className={`${MENU_ITEM_CLASSES} flex items-center justify-between gap-4 data-[state=open]:bg-neutral-1100 data-[state=open]:text-brand-white`}
      >
        Switch Organizations
        <ChevronRightIcon aria-hidden />
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          className={`${MENU_CONTENT_CLASSES} max-h-[65vh] overflow-y-auto`}
          sideOffset={4}
          collisionPadding={16}
        >
          <DropdownMenu.Label className="pl-10 pr-5 text-base font-semibold text-neutral-1300">
            Switch Organization
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={currentOrgId}>
            {orgs.map(({ orgId, orgName }) =>
              orgName ? (
                <DropdownMenu.RadioItem
                  key={orgId}
                  value={orgId}
                  className={`${MENU_ITEM_CLASSES} relative pl-10`}
                  onSelect={() => onSelectOrg(orgId, orgName)}
                >
                  <DropdownMenu.ItemIndicator className="absolute left-4 top-1/2 -translate-y-1/2">
                    <DotFilledIcon aria-hidden />
                  </DropdownMenu.ItemIndicator>
                  {orgName}
                </DropdownMenu.RadioItem>
              ) : null
            )}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
}

export function StudentProfileMenu() {
  const { data: user } = useGetMeApi();
  const submit = useSubmit();
  const { switchingToOrgName, switchOrg, orgs, currentOrgId, canSwitch } =
    useSwitchOrganization();

  const isClassroomPassword =
    user?.currentRole === 'student' && user?.userSource === 'MyScholastic';

  const onSignOut = () => {
    submit(null, { method: 'post', action: '/sign-out' });
  };

  const avatar = isClassroomPassword ? (
    <ProfileAvatarPlaceholder aria-hidden className="h-11 w-11" />
  ) : (
    user?.givenName &&
    user?.familyName && (
      <Avatar
        aria-hidden
        name={`${user.givenName} ${user.familyName}`}
        variant="medium"
        className={AVATAR_CLASSES}
      />
    )
  );

  return (
    <>
      {switchingToOrgName && <SwitchingOverlay orgName={switchingToOrgName} />}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className={TRIGGER_CLASSES}
            aria-label="Profile"
          >
            {avatar}
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content className={MENU_CONTENT_CLASSES} align="end">
            <div className="flex items-center gap-2 px-5">
              {avatar}
              {user && (
                <div className="max-w-[240px] bg-brand-white">
                  <p className="truncate font-semibold capitalize text-neutral-1300">
                    {isClassroomPassword
                      ? `${user.givenName} Classroom`
                      : `${user.givenName} ${user.familyName}`}
                  </p>
                  {!isClassroomPassword && (
                    <p className="truncate text-sm capitalize text-neutral-900">
                      {user.currentRole}
                    </p>
                  )}
                </div>
              )}
            </div>

            {canSwitch && (
              <OrgSubMenu
                orgs={orgs}
                currentOrgId={currentOrgId}
                onSelectOrg={switchOrg}
              />
            )}

            <DropdownMenu.Item
              className={MENU_ITEM_CLASSES}
              onSelect={onSignOut}
            >
              Sign Out
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </>
  );
}
